<template>
  <section class="section">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ App.MenuAdm.MIGRATION_PROCESS }}
      </h1>
      <div class="header-buttons column">
        <b-button
          tag="router-link"
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-py-15 spacing-mr-5"
          :expanded="isMobile"
          :to="goToMigrateProcess()"
          >{{ App.MigrationProcess.MIGRATION_PROCESS }}</b-button
        >
      </div>
    </div>

    <div class="is-flex">
      <GenericPanelSearch
        class="flex-1"
        :filterList="filters"
        :filterOptions="getFilterOptions"
        :tabPersistName="tabPersistName"
        :placeholderInput="General.PLACEHOLDER.SEARCH_BY_ORGAN_ORIGIN_DESTINY"
        @filters="setFilterList"
        @search-value="setSearchValue"
      />
    </div>

    <div class="level-left has-text-left m-10 mt-30">
      <span class="has-text-color-7 has-text-weight-semibold">
        {{ getPaginationDetails }}
      </span>
    </div>

    <section class="spacing-mb-20">
      <b-table
        v-if="observerPage"
        class="custom-table-administration custom-5-columns-migrations"
        :data="getMigrationsList.list"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSort="sortOrder"
        :defaultSortDirection="defaultSortOrder"
        backendSorting
        type="is-black"
        hoverable
        @sort="onSort"
      >
        <template>
          <b-table-column
            v-slot="props"
            :label="App.MigrationProcess.MIGRATION_FROM"
            cellClass="has-text-weight-semibold"
            :sortable="!isMobile"
            field="from_capacity_abbreviations"
          >
            <span
              v-for="(capacity, index) in props.row.from_capacity_abbreviations"
              :key="index"
              class="has-text-color-8"
              >{{
                `${capacity}${
                  props.row.from_capacity_abbreviations.length == index + 1
                    ? ``
                    : `, `
                }`
              }}</span
            >
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.TO"
            cellClass="has-text-weight-semibold"
            :sortable="!isMobile"
            field="to_capacity_abbreviation"
          >
            <span class="has-text-color-8">{{
              props.row.to_capacity_abbreviation
            }}</span>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="App.MigrationProcess.MIGRATED_PROCESSES"
          >
            <span class="has-text-color-8"
              >{{ props.row.process_count }}
              <template v-if="props.row.status === migrationStatus.PENDING"
                >({{ General.ESTIMATED }})</template
              ></span
            >
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.DATE"
            :sortable="!isMobile"
            field="date"
          >
            <span class="has-text-color-8">
              <template v-if="props.row.status === migrationStatus.PENDING">{{
                App.MigrationProcess.SCHEDULED_TO
              }}</template>
              {{
                new Date(props.row.date + "T00:00") | dateFormat("DD/MM/YYYY")
              }}
              <template v-if="props.row.status === migrationStatus.PENDING"
                >às 22:00</template
              >
              <template v-if="props.row.status === migrationStatus.CANCELED"
                ><strong>({{ General.CANCELED_2 }})</strong></template
              >
            </span>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="isMobile ? '' : General.ACTIONS"
          >
            <ActionsGrouper :actions="actionList(props.row)" />
          </b-table-column>
        </template>

        <template #empty>
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>
      </b-table>
    </section>

    <div
      class="is-flex mb-40"
      :class="{ 'is-flex-direction-column-reverse': isMobile }"
    >
      <b-dropdown
        v-model="perPage"
        aria-role="list"
        position="is-top-right"
        :class="{ 'spacing-mb-40': isMobile }"
        :expanded="isMobile"
        @change="currentPage = 1"
      >
        <template slot="trigger">
          <b-button class="custom-dropdown-button" :expanded="isMobile">
            {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}

            <b-icon class="spacing-mx-5" icon="chevron-down" />
          </b-button>
        </template>
        <div class="spacing-p-5 has-text-centered">
          {{ General.RECORDS_FOR_PAGE }}
          <div class="list-page">
            <b-dropdown-item
              v-for="option in optionsPagination"
              :key="option"
              :value="option"
            >
              <div>
                {{ option }}
              </div>
            </b-dropdown-item>
          </div>
        </div>
      </b-dropdown>

      <div class="custom-pagination-container" :class="{ 'mb-10': isMobile }">
        <b-pagination
          :total="getMigrationsList.count"
          :perPage="perPage"
          :current.sync="currentPage"
          rangeBefore="1"
          rangeAfter="1"
          order="is-centered"
          class="process-panel-pagination"
          rounded
          :iconPrev="prevIcon"
          :iconNext="nextIcon"
          :aria-next-label="General.NEXT_PAGE"
          :aria-previous-label="General.PREVIOUS_PAGE"
          :aria-page-label="General.PAGE"
          :aria-current-label="General.CURRENT_PAGE"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapGetters } from "vuex";
import { App, General } from "@/utils/constants";
import NoListMessage from "@/components/NoListMessage";
import GenericPanelSearch from "@/components/GenericPanelSearch";
import ModalWarning from "@/views/App/ModalWarning.vue";

import { dateToISOString } from "@/utils/timeUtility";
import {
  USER_PERMISSIONS as userPermissions,
  TAB_PERSIST_NAME as PERSIST_NAME,
  MIGRATION_STATUS as migrationStatus
} from "@/utils/enumerate";
import { setPersistSort } from "@/plugins/indexDB";
import { formatPagination } from "@/utils/transformers";

export default {
  name: "Migrations",
  components: {
    NoListMessage,
    GenericPanelSearch
  },
  data() {
    return {
      App,
      General,
      migrationStatus,
      isLoading: false,
      isFetching: false,
      observerPage: true,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      defaultSortOrder: "desc",
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      sortField: "date",
      sortOrder: ["date", "desc"],
      searchValue: "",
      filters: {}
    };
  },
  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getMigrationsList: "getMigrationsList",
      optionsPagination: "getItensPage",
      hasPermission: "hasPermission",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    getPaginationDetails() {
      return formatPagination(
        this.getMigrationsList,
        this.currentPage,
        this.perPage
      );
    },

    requestBaseData() {
      const date = {};
      if (this.filters?.date?.start && this.filters?.date?.end) {
        date.start = dateToISOString(this.filters?.date.start);
        date.end = dateToISOString(this.filters?.date.end);
      }

      return {
        paginator_count: this.perPage,
        page: this.currentPage,
        search: this.searchValue,
        date,
        order_by: this.sortField
      };
    },

    getFilterOptions() {
      return {
        DATE: {
          name: General.PERIOD,
          type: "DATE",
          key: "date",
          unique: true
        }
      };
    },

    tabPersistName() {
      return `${PERSIST_NAME.MIGRATIONS}_${this.user.id}`;
    }
  },
  watch: {
    requestBaseData: {
      handler() {
        this.loadList();
      }
    }
  },
  async beforeMount() {
    this.observerPage = false;
    const defaultSort = JSON.stringify({
      [this.sortField]: this.defaultSortOrder
    });
    this.sortOrder = await this.getSortPersist({
      sortField: defaultSort,
      tabPersistName: this.tabPersistName
    });
    this.observerPage = true;
    this.onSort(...this.sortOrder);

    this.loadList();
  },
  methods: {
    ...mapActions({
      requestMigrationsList: "requestMigrationsList",
      getSortPersist: "getPersistSortAction",
      requestMigration: "requestMigration",
      cancelMigrateProcess: "cancelMigrateProcess"
    }),

    onSort(field, order) {
      this.sortField = order !== this.defaultSortOrder ? field : `-${field}`;
      setPersistSort({
        name: this.tabPersistName,
        sort: { [field]: order }
      });
    },

    setFilterList(values) {
      this.filters = values;
    },

    goToMigrateProcess() {
      if (
        this.hasPermission(userPermissions["MIGRATE_PROCESS_ADM"]) ||
        this.hasPermission(userPermissions["MIGRATE_PROCESS_SECTOR"])
      )
        return "/app/admin/migracao-processos";
      return "";
    },

    loadList: debounce(async function() {
      this.isLoading = true;
      if (this.observerPage)
        await this.requestMigrationsList(this.requestBaseData);
      this.isLoading = false;
    }, 1000),

    setSearchValue: debounce(async function(valueForSearch) {
      this.isFetching = true;
      this.searchValue = valueForSearch;
      if (this.currentPage != 1) this.currentPage = 1;
      this.isFetching = false;
    }, 1000),

    async downloadPDF(migrationId) {
      this.isLoading = true;
      await this.requestMigration(migrationId);
      this.isLoading = false;
    },

    confirmCancelMigrateProcess(migrationId) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}`,
          message: `${App.MigrationProcess.MESSAGES.WARNING}`,
          confirm: () => {
            const status = this.cancelMigrateProcess(migrationId);
            if (status) {
              this.snackToast.open({
                message: App.MigrationProcess.MESSAGES.CANCEL_SUCCESS,
                type: "is-success",
                duration: 8000
              });
              this.loadList();
            }
          },
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ],
          isMessageCentered: true,
          invertOutline: true
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    actionList(item) {
      return [
        {
          label: this.App.MigrationProcess.DOWNLOAD_PDF_LABEL,
          icon: "download",
          position: this.isMobile ? "is-right" : "is-top",
          validated: item.status === migrationStatus.FINISHED,
          callback: () => this.downloadPDF(item.id)
        },
        {
          label: this.App.MigrationProcess.CANCEL_MIGRATION,
          icon: "cancel",
          position: this.isMobile ? "is-right" : "is-top",
          validated: item.status === migrationStatus.PENDING,
          callback: () => this.confirmCancelMigrateProcess(item.id)
        }
      ];
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding: 20px 1.5rem 0 1.5rem;

  .header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}
</style>
